import type { ComponentPropsWithoutRef, ElementType, ForwardedRef, JSX, ReactNode, Ref } from 'react';
import type { FieldValues, Path } from 'react-hook-form';

import { cn } from '@pesto/utils';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Select';
import { genericForwardRef } from '../../utils/composition';

type FormSelectBaseProps<TFieldValues extends FieldValues> = {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: string;
  description?: ReactNode;
  required?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  selectClassName?: string;
  id?: string;
  name?: Path<TFieldValues>;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  onChange?: (value: string) => void;
  loading?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
};

export type FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  T extends ElementType = typeof Select,
> = FormSelectBaseProps<TFieldValues> & {
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof FormSelectBaseProps<TFieldValues>>;

const FormSelectInner = <TFieldValues extends FieldValues = FieldValues, T extends ElementType = typeof Select>(
  {
    label,
    helperText,
    error,
    description,
    required,
    wrapperClassName,
    labelClassName,
    selectClassName,
    onChange,
    id: providedId,
    as,
    className,
    name,
    options,
    placeholder,
    readOnly,
    disabled,
    'aria-describedby': ariaDescribedBy,
    ...props
  }: FormSelectProps<TFieldValues, T>,
  ref: ForwardedRef<HTMLButtonElement>,
) => {
  const id = providedId || `form-field-${name}`;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const descriptionId = `${id}-description`;

  const describedBy = [ariaDescribedBy, helperText && helperId, error && errorId, description && descriptionId]
    .filter(Boolean)
    .join(' ');

  const Component = as || Select;

  const handleOnChange = (value: string) => {
    onChange?.(value);
  };

  return (
    <div className={cn('space-y-2', wrapperClassName)}>
      {label && (
        <div className="flex justify-between">
          <label
            htmlFor={id}
            className={cn(
              'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
              error && 'text-destructive',
              labelClassName,
            )}
          >
            {label}
            {required && !readOnly && <span className="text-destructive ml-1">*</span>}
          </label>
          {description && (
            <span id={descriptionId} className="text-muted-foreground text-xs">
              {description}
            </span>
          )}
        </div>
      )}
      <Component name={name} disabled={readOnly || disabled} onValueChange={handleOnChange} {...props}>
        <SelectTrigger
          ref={ref}
          id={id}
          className={cn(error && 'border-destructive', selectClassName, className)}
          readOnly={readOnly}
          aria-describedby={describedBy || undefined}
          aria-invalid={!!error}
          aria-required={required}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(({ value, label: optionLabel }) => (
            <SelectItem key={value} value={value}>
              {optionLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Component>
      {(helperText || error) && (
        <div className="space-y-1">
          {helperText && (
            <p id={helperId} className="text-muted-foreground text-sm">
              {helperText}
            </p>
          )}
          {error && (
            <p id={errorId} className="text-destructive text-sm">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// SAFETY: forwardRef only attaches the ref; props reach FormSelectInner unchanged, so its generic signature holds.
export const FormSelect = genericForwardRef(FormSelectInner, 'FormSelect') as <
  TFieldValues extends FieldValues = FieldValues,
  T extends ElementType = typeof Select,
>(
  props: FormSelectProps<TFieldValues, T> & { ref?: Ref<HTMLButtonElement> },
) => JSX.Element;
